import React, { useState, useEffect, useCallback } from 'react';

import { v2, Banner, Loader } from '@fieldnation/platform-components';
import styles from './ReviewDispatchSettingsModal.scss';
import { ReviewDispatchSettingsModalProps } from './types.d';
import { getDispatchSettings, getDispatchExpansionSettings } from '../utils/dispatchSettingsApi';
import formatDispatchDuration from './utils';

const { Modal } = v2;

// Extend Banner type to include action prop that exists in runtime
const ExtendedBanner = Banner as React.ComponentType<
  React.ComponentProps<typeof Banner> & {
    action?: {
      text: string;
      href: string;
      target?: '_blank' | '_self' | '_parent' | '_top';
    };
  }
>;

export const ReviewDispatchSettingsModal = ({
  isOpen,
  projectId,
  projectName,
  workOrderPriorityValue,
  workOrderTemplateId,
  isSmartDispatchV3 = false,
  onClose,
  onSubmit,
}: ReviewDispatchSettingsModalProps): JSX.Element => {
  const [isError, setIsError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [allSegmentsDisabled, setAllSegmentsDisabled] = useState(false);
  const [dispatchData, setDispatchData] = useState<{
    dispatchDuration: number;
    priorityOverride: { priorityName: string } | null;
    unassignedWorkOrders: string;
  } | null>(null);

  const fetchDispatchSettings = useCallback(async () => {
    setLoading(true);
    setIsError(false);
    setAllSegmentsDisabled(false);

    try {
      const expansionPromise = isSmartDispatchV3
        ? getDispatchExpansionSettings(projectId).catch(() => null)
        : Promise.resolve(null);

      const [data, expansionData] = await Promise.all([
        getDispatchSettings(projectId),
        expansionPromise,
      ]);

      // Find matching reporting target for priority-based dispatch duration override
      // BOTH conditions are REQUIRED: priority MUST match AND template MUST match
      const matchedTarget = workOrderPriorityValue && Array.isArray(data?.reporting_targets)
        ? data.reporting_targets.find((t) => {
          // Requirement 1: Work order priority must exactly match reporting target priority (case-insensitive)
          if (t.priority?.toLowerCase() !== workOrderPriorityValue.toLowerCase()) return false;
          // Requirement 2: Duration must be valid (cannot be negative)
          if (t.dispatch_duration_minutes < 0) return false;
          // Requirement 3: Template must be defined in reporting target
          if (!Array.isArray(t.template_ids) || t.template_ids.length === 0) return false;
          // Requirement 4: Work order template must be in the priority level's approved template list
          return workOrderTemplateId != null && t.template_ids.includes(Number(workOrderTemplateId));
        }) ?? null
        : null;

      setDispatchData({
        dispatchDuration: matchedTarget?.dispatch_duration_minutes ?? data?.dispatch_runtime_minutes ?? 0,
        priorityOverride: matchedTarget
          ? { priorityName: matchedTarget.priority }
          : null,
        unassignedWorkOrders: data?.revert_to_draft_on_no_available
          ? 'Revert to draft'
          : 'Remain published',
      });

      if (isSmartDispatchV3 && expansionData !== null) {
        const exp = expansionData?._included?.expansionSettings;
        setAllSegmentsDisabled(
          !exp?.serviceTerritory?.enabled
          && !exp?.marketplace?.enabled
          && !exp?.talentPools?.enabled,
        );
      }
    } catch (error) {
      setDispatchData(null);
      setIsError(true);
    } finally {
      setLoading(false);
    }
  }, [projectId, workOrderPriorityValue, workOrderTemplateId, isSmartDispatchV3]);

  useEffect(() => {
    if (isOpen) {
      fetchDispatchSettings();
    }
  }, [isOpen, fetchDispatchSettings]);

  const showNoSegmentsBanner = isSmartDispatchV3 && allSegmentsDisabled;

  const renderContent = () => {
    if (isError) {
      return (
        <div className={styles.error_placeholder}>
          <ExtendedBanner
            type="alert"
            message="An error occurred while fetching dispatch settings. Please try again later."
          />
        </div>
      );
    }

    if (!dispatchData) {
      return <div className={styles.empty_placeholder} />;
    }

    return (
      <div className={styles.review_dispatch_settings_modal}>
        <div className={styles.smart_dispatch_section}>
          {projectName && (
            <>
              <div className={styles.smart_dispatch_row}>
                <strong>Smart Dispatch: </strong>
                {projectName}
              </div>
              <hr className={styles.section_divider} />
            </>
          )}
          <div className={styles.smart_dispatch_row}>
            <span className={styles.dispatch_label}>Dispatch duration:</span>
            {formatDispatchDuration(dispatchData.dispatchDuration)}
            {dispatchData?.priorityOverride && (
              <span>
                {' '}
                (
                {dispatchData.priorityOverride.priorityName}
                {' '}
                override)
              </span>
            )}
          </div>
          <div className={styles.dispatch_detail_row}>
            <span className={styles.dispatch_label}>
              Unassigned work orders:
            </span>
            {dispatchData.unassignedWorkOrders}
          </div>
        </div>

        {showNoSegmentsBanner && (
          <ExtendedBanner
            type="alert"
            title="Smart Dispatch Cannot run"
            message="No provider segments are enabled. You must enable at least one segment under the segments & criteria tab."
            action={{
              text: 'Go to project dispatch settings',
              href: `/projects/${projectId}/manage-project?tab=smart-dispatch`,
              target: '_blank',
            }}
          />
        )}

        {!showNoSegmentsBanner && <ExtendedBanner
          type="info"
          message="To modify the Smart Dispatch settings for this work order, please update the project settings."
          action={{
            text: 'Go to project dispatch settings',
            href: `/projects/${projectId}/manage-project?tab=smart-dispatch`,
            target: '_blank',
          }}
        />}
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      header="Review Dispatch Settings"
      size="medium"
      type="secondary"
      footerActions={[
        {
          label: 'Cancel',
          type: 'secondary',
          onClick: onClose,
          disabled: loading,
        },
        {
          label: 'Dispatch Work Order',
          type: 'primary',
          onClick: onSubmit,
          disabled: isError || loading || showNoSegmentsBanner,
        },
      ]}
    >
      <div>
        <Loader isLoading={loading}>{renderContent()}</Loader>
      </div>
    </Modal>
  );
};

export default ReviewDispatchSettingsModal;
